import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    EMPTY_ARRAY_LENGTH,
    IAumMenuItem,
    IColumn,
    ITableOptions,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
} from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { Observable, filter } from 'rxjs';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import { localizationKeys } from '../../constants/localizationKeys';
import { GetUserDto } from '../../dtos/getUserDto.type';
import { OrganizationUserRole, OrganizationUserRoleLabel } from '../../enums/organization-user-roles.enum';
import { RemoveUsersConfirmationDialogActions } from '../../enums/remove-users-confirmation-dialog-actions.enum';
import { IOrganizationUserTableData } from '../../interfaces/organization-user-table-data.interface';
import { OrganizationProvider } from '../../provider/organisation.provider';

const REMOVE_USERS_ACTION = 'remove_users';
@UntilDestroy()
@Component({
    selector: 'aumico-organization-users-page',
    templateUrl: './organization-users.page.html',
    styleUrls: ['./organization-users.page.scss'],
})
export class OrganizationUsersPage {
    protected options: ITableOptions;
    private selectedUserIds: string[] = [];

    selectedRows: IOrganizationUserTableData[] = [];
    private _filterText: string = '';
    public filteredData: IOrganizationUserTableData[];

    public menuItems: IAumMenuItem[] = [];
    currentSelected: string;
    invitedUsers: string[] = [];
    openedRoleFlyoutUserId: string | null = null;
    flyoutPosition: { top: number; right?: number; left?: number; isAbove?: boolean };
    organizationUserRoles = Object.values(OrganizationUserRole);
    organizationUserRolesLabels = OrganizationUserRoleLabel;

    // Dropdown options for bulk edit
    get roleDropdownOptions() {
        return this.organizationUserRoles.map(role => ({
            id: role,
            label: this.organizationUserRolesLabels[role],
            value: role
        }));
    }
    private readonly DEFAULT_FLYOUT_HEIGHT = 200; // Default estimate
    private readonly DEFAULT_FLYOUT_WIDTH = 200; // Default estimate

    get filterText(): string {
        return this._filterText;
    }

    set filterText(value: string) {
        this._filterText = value.toLowerCase();
        this.applyFilter();
    }

    @Output() search = new EventEmitter<string>();
    @Output() addNewUserClicked = new EventEmitter<void>();

    searchForm: FormGroup;
    showWithdrawModal: boolean = false;
    showResendInviteModal: boolean = false;
    showDeletionUserModal: boolean = false;

    @Input() set users(value: GetUserDto[]) {
        this.tableUsers = this.mapGetUserDtoToIOrganizationUserTableData(value);
        this.applyFilter();
        this.updateTableHeight();
    }

    protected tableUsers: IOrganizationUserTableData[] = [];
    private local = localizationKeys;
    public columns: IColumn[];
    public errorMessage: Observable<string>;

    // Variables for bulk edit
    bulkActions = [{ id: 'ROLE_CHANGE', translationKey: 'aumico_orga_settings_role_change_action_bar' }];
    deleteAction = { id: 'DELETE_USERS', translationKey: 'aumico_users_organization_users_table_delete_action_bar' };

    openBulkEditRole: boolean = false;
    bulkEditRoleControl = this.fb.control(null);
    clearSelectionsFlag: boolean = false;

    constructor(
        private readonly localizedTexts: I18NextService,
        private readonly dialog: MatDialog,
        private readonly organizationProvider: OrganizationProvider,
        private readonly fb: FormBuilder,
    ) {
        this.searchForm = this.fb.group({
            searchTerm: [''],
        });
        this.getMenuOptions();
    }
    @ViewChild('pageWrapper') pageWrapper!: ElementRef;

    @HostListener('window:resize', [])
    onWindowResize(): void {
        this.updateTableHeight();
    }

    private updateTableHeight(): void {
        if (this.pageWrapper && this.pageWrapper.nativeElement) {
            const pageWrapperElement = this.pageWrapper.nativeElement as HTMLElement;
            const rect = pageWrapperElement.getBoundingClientRect();
            const distanceFromTop = rect.top;

            // Calculate the available height: 100vh minus the distance from top
            const staticHeightOfTopNavBar = 35; // Adjust this value based on your layout
            const availableHeight = window.innerHeight - distanceFromTop - staticHeightOfTopNavBar;
            // Set the CSS custom property with the calculated height
            // We subtract a small buffer (like 20px) to prevent scrollbars
            const tableMaxHeight = Math.max(200, availableHeight); // Minimum 200px

            pageWrapperElement.style.setProperty('--ham-table-max-h', `${tableMaxHeight}px`);
        }
    }
    private mapGetUserDtoToIOrganizationUserTableData(users: GetUserDto[]): IOrganizationUserTableData[] {
        const tableUsers: IOrganizationUserTableData[] = [];
        users.forEach((user) => {
            const name = user.firstName + ' ' + user.lastName;

            tableUsers.push({
                id: user.id,
                name: name,
                email: user.email,
                phoneNumber: user.phoneNumber,
                photoURL: user.photoURL,
                createdAt: user.createdAt,
                isInvitee: user.isInvitee,
                organizationRole: user.organizationRole,
                isMe: user.isMe,
            });
        });
        return tableUsers;
    }

    private getMenuOptions(): void {
        this.menuItems = [
            {
                caption: this.localizedTexts.t(this.local.aumico_users_organization_users_table_remove_users) as string,
                action: REMOVE_USERS_ACTION,
                icon: 'assets/hamIcons/trash-outline.svg',
                // set to true in case we want it disabled if no rows selected
                disabled: true,
            },
        ];
    }

    public onMenuItemClicked(action: string): void {
        if (
            action === REMOVE_USERS_ACTION &&
            this.selectedRows != null &&
            this.selectedRows.length > EMPTY_ARRAY_LENGTH
        ) {
            this.selectedRows.forEach((selectedUser: IOrganizationUserTableData) => {
                if (selectedUser.isInvitee) {
                    this.invitedUsers.push(selectedUser.id);
                } else {
                    this.selectedUserIds.push(selectedUser.id);
                }
            });
            this.openRemoveUsersConfirmationDialog();
        }
    }

    openRoleFlyout(userId: string, buttonComponent: HamButtonComponent): void {
        if (this.openedRoleFlyoutUserId === userId) {
            this.closeRoleFlyout();
        } else {
            this.openedRoleFlyoutUserId = userId;
            const buttonElement = buttonComponent.nativeButton?.nativeElement;
            this.flyoutPosition = this.calculateFlyoutPosition(buttonElement.getBoundingClientRect());
        }
    }

    closeRoleFlyout(): void {
        this.openedRoleFlyoutUserId = null;
    }

    confirmRoleChange(user: IOrganizationUserTableData, role: OrganizationUserRole): void {
        this.organizationProvider.dispatchUpdateUserRole({ userId: user.id, role: role });
        this.closeRoleFlyout();
    }

    private calculateFlyoutPosition(
        buttonRect: DOMRect,
        flyoutWidth: number = this.DEFAULT_FLYOUT_WIDTH,
        flyoutHeight: number = this.DEFAULT_FLYOUT_HEIGHT,
    ): { top: number; left: number; right: number; isAbove: boolean } {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let top: number;
        let isAbove = false;

        if (spaceBelow >= flyoutHeight || spaceBelow >= spaceAbove) {
            top = buttonRect.bottom;
        } else {
            top = buttonRect.top - flyoutHeight;
            isAbove = true;
        }

        const left = buttonRect.left;
        const right = window.innerWidth - buttonRect.right;

        return { top, left, right, isAbove };
    }

    filterItems(): void {
        this.filterText = this.searchForm.get('searchTerm').value;
        this.applyFilter();
        this.search.emit();
    }

    public resetFilter(): void {
        this.filterText = '';
    }

    public onCheckBoxSelectionChanged(selectedRows: IOrganizationUserTableData[]): void {
        this.selectedRows = selectedRows;
        this.selectedUserIds = [];
    }

    private openRemoveUsersConfirmationDialog(): void {
        if (this.dialog == null) {
            throw new Error(`dialog is undefined`);
        }

        const dialogRef = this.dialog.open<ModalComponent, ModalData, RemoveUsersConfirmationDialogActions>(
            ModalComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.DEFAULT,
                    footerButtons: [
                        {
                            localizationKey: 'aumico_users_organization_users_remove_confirmation_button',
                            action: RemoveUsersConfirmationDialogActions.REMOVE,
                            disabled: false,
                            buttonColor: 'primary',
                        },
                        {
                            localizationKey: 'aumico_users_organization_users_remove_cancel_button',
                            action: RemoveUsersConfirmationDialogActions.CANCEL,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: ['aumico_users_organization_users_remove_confirmation_text'],
                        icon: 'aumico-pattern-icon-trash-bin',
                    },
                },
            },
        );

        dialogRef
            .afterClosed()
            .pipe(
                filter((action) => action != null),
                untilDestroyed(this),
            )
            .subscribe((action) => {
                if (action != RemoveUsersConfirmationDialogActions.REMOVE) {
                    return;
                }

                if (this.selectedUserIds.length > 0) {
                    this.organizationProvider.dispatchRemoveUsers(this.selectedUserIds);
                }

                if (this.invitedUsers.length > 0) {
                    this.organizationProvider.dispatchWithdrawInvite(this.invitedUsers);
                }

                this.invitedUsers = [];
                this.selectedUserIds = [];
            });
    }

    private applyFilter(): void {
        if (this._filterText) {
            this.filteredData = this.tableUsers.filter((item) => item.name.toLowerCase().includes(this._filterText));
        } else {
            this.filteredData = [...this.tableUsers];
        }
    }

    public addNewUser(): void {
        this.addNewUserClicked.emit();
    }

    public withdrawInvite(): void {
        this.organizationProvider.dispatchWithdrawInvite([this.currentSelected]);
        this.showWithdrawModal = false;
        this.currentSelected = null;
    }

    onShowWithdrawModal(invitee: string): void {
        this.showWithdrawModal = true;
        this.currentSelected = invitee;
    }

    onCancelWithdraw(): void {
        this.showWithdrawModal = false;
        this.currentSelected = null;
    }

    resendInvite(): void {
        this.organizationProvider.dispatchResendInvitation(this.currentSelected);
        this.showResendInviteModal = false;
        this.currentSelected = null;
    }

    onResendInvite(invitee: string): void {
        this.showResendInviteModal = true;
        this.currentSelected = invitee;
    }

    onCancelResendInvitation(): void {
        this.showResendInviteModal = false;
        this.currentSelected = null;
    }

    onShowDeleteModal(userId: string): void {
        this.showDeletionUserModal = true;
        this.currentSelected = userId;
    }

    onCancelDeletion(): void {
        this.showDeletionUserModal = false;
        this.currentSelected = null;
    }

    deleteUser(): void {
        this.organizationProvider.dispatchRemoveUsers([this.currentSelected]);
        this.showDeletionUserModal = false;
        this.currentSelected = null;
    }

    clearAllSelections(): void {
        this.selectedRows = [];
        this.clearSelectionsFlag = true;
        // Reset the flag after a short delay to allow for future clears
        setTimeout(() => {
            this.clearSelectionsFlag = false;
        }, 100);
    }

    /**
     * Filter out invitees from selected rows since backend operations don't work on them
     */
    private getValidUsersForBackendAction(selectedRows: IOrganizationUserTableData[]): IOrganizationUserTableData[] {
        return selectedRows.filter(user => !user.isInvitee);
    }

    // Bulk actions
    onActionClicked(actionId: string): void {
        if (actionId === 'ROLE_CHANGE') {
            this.openBulkEditRole = true;
        } else if (actionId === 'DELETE_USERS') {
            // Filter out invitees before sending to backend
            const validUsers = this.getValidUsersForBackendAction(this.selectedRows);
            this.organizationProvider.dispatchRemoveUsers(validUsers.map(user => user.id));
            this.clearAllSelections();
        } else if (actionId === 'CLOSE_BULK_EDIT') {
            this.clearAllSelections();
        }
    }

    onEditRoleConfirm(): void {
        const selectedRole = this.bulkEditRoleControl.value;
        if (selectedRole && this.selectedRows.length > 0) {
            // Filter out invitees and current user before sending to backend
            const validUsers = this.getValidUsersForBackendAction(this.selectedRows);
            const usersToUpdate = validUsers.filter(user => !user.isMe);

            if (usersToUpdate.length > 0) {
                const userIds = usersToUpdate.map(user => user.id);
                // Use bulk update instead of individual calls
                this.organizationProvider.dispatchBulkUpdateUserRole({
                    userIds,
                    role: selectedRole
                });
            }

            // Close modal and reset
            this.openBulkEditRole = false;
            this.bulkEditRoleControl.reset();
            this.clearAllSelections();
        }
    }
}
